import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import { DataOptions } from "vuetify";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { AutomaticReport, AutomaticReportAdd, AutomaticReportEdit } from "@/models/api/report/AutomaticReport";
import {
  createAutomaticReport,
  deleteAutomaticReport,
  editAutomaticReport,
  getAutomaticReports,
  getEmailAddressesToSendReport,
} from "@/requests/report/automaticReport";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { SelectItem } from "@/models/api/SelectItem";

export interface AutomaticReportsStateInterface {
  reportFilters: Array<CriterionItemRequest> | null;
  automaticReports: Array<AutomaticReport>;
  automaticReportsCount: number | null;
  tableOptions: DataOptions;
  isLoading: boolean;
  recordStampId: null | number;
  sendFromMailSelectItems: Array<SelectItem> | null;
}

export const AutomaticReportsState: AutomaticReportsStateInterface = {
  reportFilters: null,
  automaticReports: [],
  automaticReportsCount: null,
  tableOptions: new DefaultDataOptions(),
  isLoading: true,
  recordStampId: null,
  sendFromMailSelectItems: null,
};

const AutomaticReportsModule = defineModule({
  state: AutomaticReportsState as AutomaticReportsStateInterface,
  namespaced: true as const,
  modules: {},
  getters: {},
  mutations: {
    setReportFilters(state, param: Array<CriterionItemRequest> | null) {
      state.reportFilters = param;
    },
    setAutomaticReports(state, param: Array<AutomaticReport>) {
      state.automaticReports = param;
    },
    setAutomaticReportsCount(state, param: number | null) {
      state.automaticReportsCount = param;
    },
    setTableOptions(state, param: DataOptions) {
      state.tableOptions = param;
    },
    setIsLoading(state, param: boolean) {
      state.isLoading = param;
    },
    setRecordStampId(state, param: number | null) {
      state.recordStampId = param;
    },
    setSendFromMailSelectItems(state, param: Array<SelectItem>) {
      state.sendFromMailSelectItems = param;
    },
  },
  actions: {
    async downloadAutomaticReports(context) {
      const { commit, state, rootState } = _moduleActionContext(context);
      commit.setIsLoading(true);
      const reports = await getAutomaticReports(
        rootState.reports.activeReportType[0],
        state.tableOptions.page - 1,
        state.tableOptions.itemsPerPage
      );
      commit.setAutomaticReports(reports.items);
      commit.setAutomaticReportsCount(reports.totalCount);
      commit.setIsLoading(false);
    },
    async createAutomaticReport(context, editModel: AutomaticReportAdd) {
      const { state, rootState, dispatch } = _moduleActionContext(context);
      if (rootState.reports.activeReportFilterName) {
        await createAutomaticReport(state.reportFilters, rootState.reports.activeReportFilterName, editModel);
        dispatch.downloadAutomaticReports();
      }
    },
    async editAutomaticReport(context, editModel: AutomaticReportEdit) {
      const { state, rootState, dispatch } = _moduleActionContext(context);
      if (rootState.reports.activeReportFilterName) {
        await editAutomaticReport(state.reportFilters, rootState.reports.activeReportFilterName, editModel);
        dispatch.downloadAutomaticReports();
      }
    },
    async getEmailAddressToSend(context) {
      const { commit } = _moduleActionContext(context);
      const result = await getEmailAddressesToSendReport();
      commit.setSendFromMailSelectItems(result);
    },
    async deleteAutomaticReport(context, scheduleId: number) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      rootDispatch.failIfRequestsInProgress();
      const isDeleteConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(i18n.t("areYouSureYouWantToDeleteThisAutomaticReport")),
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "500px"
        )
      )) as boolean;
      if (isDeleteConfirmed) {
        await deleteAutomaticReport(scheduleId);
        dispatch.downloadAutomaticReports();
      }
    },
  },
});
export default AutomaticReportsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AutomaticReportsModule);
